import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ImageUpload from '@/components/ui/custom/input/ImageUpload';
import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import SecondarySelect from '@/components/ui/custom/secondary/SecondarySelect';
import { LESSON_INTO_BLOCK_MAX_PAGE_SIZE } from '@/constants';
import useListService from '@/data/hook/useListService';
import {
  courseLinesQueryKeys,
  courseModulesQueryKeys,
} from '@/data/services/querykeys';
import { courseDurationOptions } from '@/utils/courseDurationTranslation';
import { courseFormatsOptions } from '@/utils/courseFormatsTranslation';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import { CourseStudioProps } from './CourseStudioInfo.hooks';

export default function CourseStudioInfoStep(props: CourseStudioProps) {
  const { disabled = false } = props;
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CourseStudioFormFields>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const courseLineId = watch('courseLineId');

  const PAGE_SIZE = 50;

  const { results: courseLines, isInitialLoading: loadingLines } =
    useListService({
      ...courseLinesQueryKeys.list({ pageSize: PAGE_SIZE }),
    });

  const { results: courseModules, isInitialLoading: loadingModules } =
    useListService({
      ...courseModulesQueryKeys.list(courseLineId ?? 0, {
        pageSize: PAGE_SIZE,
      }),
      enabled: !!courseLineId,
    });

  const courseLineOptions = courseLines.map(line => ({
    label: line.name,
    value: line.id,
  }));

  const courseModuleOptions = courseModules.map(module => ({
    label: module.name,
    value: module.id,
  }));

  const requiredMessage = (field: string) =>
    t('required', { field: field.toLowerCase() });

  const courseFormats = courseFormatsOptions();

  const courseDuration = courseDurationOptions();

  return (
    <ConditionalWrapper
      condition={disabled}
      wrapper={
        <Tooltip
          text={t('allowFirstStudioStepMessage')}
          classNameContainer="flex w-full"
        />
      }
    >
      <div className="flex gap-2.5 w-full">
        <section className="flex flex-col gap-2.5 w-[12rem]">
          <Controller
            control={control}
            name="course.bannerImg"
            render={({ field: { onChange, value } }) => (
              <ImageUpload
                data-testid="imageUploadInput"
                disabled={disabled}
                value={value}
                onChange={onChange}
                maxSize={5 * 1024 * 1024}
                className={{
                  base: 'h-28',
                }}
              />
            )}
          />
        </section>
        <section className="flex flex-col gap-2.5 grow">
          <SecondaryInput
            testId="nameInput"
            label={t('name')}
            {...register('course.name', {
              required: requiredMessage(t('name')),
            })}
            error={errors.course?.name?.message}
            maxLength={200}
            disabled={disabled}
          />

          <Controller
            control={control}
            name="course.formats"
            rules={{
              required: requiredMessage(t('format')),
            }}
            render={({ field: { onChange, value } }) => (
              <SecondarySelect
                testId="formatSelect"
                value={value?.[0]}
                onChange={val => onChange([val])}
                label={t('format')}
                error={errors.course?.formats?.message}
                options={courseFormats}
                disabled={disabled}
              />
            )}
          />

          <SecondaryInput
            maxLength={7}
            label={t('abbreviation')}
            testId="abbreviationInput"
            {...register('course.abbreviation', {
              required: requiredMessage(t('abbreviation')),
            })}
            error={errors.course?.abbreviation?.message}
            disabled={disabled}
          />

          <Controller
            control={control}
            name="course.duration"
            rules={{
              required: requiredMessage(t('duration')),
            }}
            render={({ field: { onChange, value } }) => (
              <SecondarySelect
                testId="durationSelect"
                value={value ?? undefined}
                onChange={onChange}
                label={t('duration')}
                error={errors.course?.duration?.message}
                options={courseDuration}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name="courseLineId"
            rules={{ required: requiredMessage(t('courseLine')) }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <SecondarySelect
                testId="courseLineSelect"
                label={t('courseLine')}
                value={value}
                onChange={val => {
                  onChange(val);
                  setValue('course.module.id', 0);
                }}
                options={courseLineOptions}
                error={error?.message}
                disabled={disabled || loadingLines}
              />
            )}
          />

          <Controller
            control={control}
            name="course.module.id"
            rules={{ required: requiredMessage(t('courseModule')) }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <SecondarySelect
                testId="courseModuleSelect"
                label={t('courseModule')}
                value={value}
                onChange={onChange}
                options={courseModuleOptions}
                error={error?.message}
                disabled={disabled || !courseLineId || loadingModules}
              />
            )}
          />

          <SecondaryInput
            type="number"
            label={t('lessonsQuantity')}
            testId="lessonsQuantityInput"
            max={LESSON_INTO_BLOCK_MAX_PAGE_SIZE}
            onKeyDown={e => {
              const value = parseInt(e.currentTarget.value + e.key);
              if (value > LESSON_INTO_BLOCK_MAX_PAGE_SIZE) {
                e.preventDefault();
              }
            }}
            {...register('coursePath.lessonsQuantity', {
              max: LESSON_INTO_BLOCK_MAX_PAGE_SIZE,
              required: requiredMessage(t('lessonsQuantity')),
            })}
            error={errors.coursePath?.lessonsQuantity?.message}
            disabled={disabled}
          />
        </section>
      </div>
    </ConditionalWrapper>
  );
}
